import Phaser from 'phaser';

import { FontBuilder, FontOptions, FontStyle } from 'src/games/common/font-builder';
import { GameMainScene } from 'src/games/common/phaser/game/scenes/game-main-scene';
import { TextResizer } from 'src/games/common/phaser/game/ui/text-resizer';

const MinimumTextSize = 14;

export const AvailableFontSizes = {
  Small: '30px',
  Medium: '40px',
  Large: '60px',
  XLarge: '70px',
  XXLarge: '100px',
} as const;

export const AvailableFontColors = {
  White: '#ffffff',
  Offwhite: '#cccccc',
} as const;

export type FontSizeMapping = {
  [K in keyof typeof AvailableFontSizes]: number;
};

export class TextService {
  private textResizers: Record<keyof typeof AvailableFontSizes, TextResizer>;
  private fontSizeTextMap: Record<keyof typeof AvailableFontSizes, string>;

  constructor(
    scene: GameMainScene,
    mainContainer: Phaser.GameObjects.Container,
    private fontFamily: string,
    private availableWidth: number,
  ) {
    const fontBuilder: FontBuilder<string, string> = FontBuilder.create(
      AvailableFontSizes.Medium,
      AvailableFontColors.White,
      fontFamily,
    );
    this.textResizers = {} as Record<keyof typeof AvailableFontSizes, TextResizer>;
    this.fontSizeTextMap = {
      Small: 'XXXXXXXXXX',
      Medium: 'XXXXXXXX',
      Large: 'XXXXX',
      XLarge: 'XXXX',
      XXLarge: 'XX',
    };

    Object.entries(AvailableFontSizes).forEach(([key, value]) => {
      const typedKey = key as keyof typeof AvailableFontSizes;
      const fontStyle = fontBuilder.setFontSize(value).setFontStyle(FontStyle.Normal).build();
      this.textResizers[typedKey] = new TextResizer(scene, mainContainer, fontStyle);
    });
  }

  public refreshFontSizes(scaleFactor: number): FontSizeMapping {
    return (Object.keys(this.textResizers) as Array<keyof typeof AvailableFontSizes>).reduce(
      (acc, key) => {
        acc[key as keyof typeof AvailableFontSizes] = this.textResizers[key].fitText(
          this.fontSizeTextMap[key],
          this.availableWidth * scaleFactor,
          MinimumTextSize,
        );
        return acc;
      },
      {} as Record<keyof typeof AvailableFontSizes, number>,
    );
  }

  public static fontStyleNormal(
    fontSize: (typeof AvailableFontSizes)[keyof typeof AvailableFontSizes],
    fontColor: (typeof AvailableFontColors)[keyof typeof AvailableFontColors] = AvailableFontColors.White,
    fontFamily: string,
  ): FontOptions<string, string> {
    const fontBuilder: FontBuilder<string, string> = FontBuilder.create(
      AvailableFontSizes.Medium,
      AvailableFontColors.White,
      fontFamily,
    );
    return fontBuilder
      .setFontStyle(FontStyle.Normal)
      .setFontSize(fontSize)
      .setFontColor(fontColor)
      .build();
  }

  public static fontStyleBold(
    fontSize: (typeof AvailableFontSizes)[keyof typeof AvailableFontSizes],
    fontColor: (typeof AvailableFontColors)[keyof typeof AvailableFontColors] = AvailableFontColors.White,
    fontFamily: string,
  ): FontOptions<string, string> {
    const fontBuilder: FontBuilder<string, string> = FontBuilder.create(
      AvailableFontSizes.Medium,
      AvailableFontColors.White,
      fontFamily,
    );
    return fontBuilder
      .setFontStyle(FontStyle.Bold)
      .setFontSize(fontSize)
      .setFontColor(fontColor)
      .build();
  }
}
